"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/context/AuthContext";
import { useToast } from "@/lib/context/ToastContext";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import BrowseContent from "@/components/home/BrowseContent";
import AdditionalProfileModal from "@/components/modals/AdditionalProfileModal";
import SimpleAuthModal from "@/components/auth/SimpleAuthModal";
import { fetchWithAuthRetry } from '@/lib/utils/fetchWithAuthRetry';
import { clearAuthSession } from '@/lib/services/auth';

export default function BrowsePage() {
  const router = useRouter();
  const { user } = useAuth();
  const toast = useToast();
  const { t } = useLanguage();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [selectedCourseTitle, setSelectedCourseTitle] = useState<string | undefined>();
  const [selectedCourseType, setSelectedCourseType] = useState<'course' | 'event'>('course');

  const handleCourseRegister = async (courseId: string, courseTitle?: string, courseType?: 'course' | 'event') => {
    if (!user) {
      setSelectedCourseId(courseId);
      setSelectedCourseTitle(courseTitle);
      setSelectedCourseType(courseType || 'course');
      setShowAuthModal(true);
      return;
    }

    try {
      const profileResponse = await fetchWithAuthRetry(`/api/courses/${courseId}/profile-completion`);

      if (profileResponse.status === 401) {
        toast.error(t('authModal.authSessionRestoreFailed'), {
          duration: 15000,
          action: { label: t('authModal.authClearSessionAndRetry'), onClick: () => { clearAuthSession(); window.location.reload() } }
        });
        return;
      }

      const profileData = await profileResponse.json();

      if (!profileResponse.ok) {
        console.error('Profile check server error:', profileData.error);
        toast.error(t('course.serverErrorTryAgain'));
        return;
      }

      if (!profileData.isComplete) {
        setSelectedCourseId(courseId);
        setSelectedCourseTitle(courseTitle);
        setSelectedCourseType(courseType || 'course');
        setShowProfileModal(true);
      } else {
        const basePath = courseType === 'event' ? '/event' : '/courses';
        router.push(`${basePath}/${courseId}`);
      }
    } catch (error) {
      console.error('Profile check error:', error);
      toast.error(t('course.networkErrorCheckConnection'));
    }
  };

  const handleProfileComplete = () => {
    setShowProfileModal(false);
    if (selectedCourseId) {
      const basePath = selectedCourseType === 'event' ? '/event' : '/courses';
      router.push(`${basePath}/${selectedCourseId}`);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <BrowseContent onRegister={handleCourseRegister} variant="page" />

      {/* Additional Profile Modal */}
      {selectedCourseId && (
        <AdditionalProfileModal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          courseId={selectedCourseId}
          courseTitle={selectedCourseTitle}
          onComplete={handleProfileComplete}
        />
      )}

      {/* Auth Modal */}
      <SimpleAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultMode="signup"
      />
    </div>
  );
}
